<script setup>
import { computed, nextTick, ref } from 'vue';
import { watchDebounced } from '@vueuse/core';
import { vOnClickOutside } from '@vueuse/components';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import MessageApi from 'dashboard/api/inbox/message';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const props = defineProps({
  conversationId: { type: Number, required: true },
});

const { t } = useI18n();
const store = useStore();
const isOpen = ref(false);
const query = ref('');
const since = ref('');
const until = ref('');
const results = ref([]);
const isLoading = ref(false);

const hasFilters = computed(() => query.value.trim() || since.value || until.value);

const toTimestamp = (date, endOfDay = false) => {
  if (!date) return undefined;
  const time = endOfDay ? '23:59:59.999' : '00:00:00';
  return Math.floor(new Date(`${date}T${time}`).getTime() / 1000);
};

const searchMessages = async () => {
  if (!hasFilters.value) {
    results.value = [];
    return;
  }

  isLoading.value = true;
  try {
    const { data } = await MessageApi.search({
      conversationId: props.conversationId,
      q: query.value.trim(),
      since: toTimestamp(since.value),
      until: toTimestamp(until.value, true),
    });
    results.value = data.payload || [];
  } finally {
    isLoading.value = false;
  }
};

watchDebounced([query, since, until], searchMessages, {
  debounce: 350,
  maxWait: 800,
});

const toggle = () => {
  isOpen.value = !isOpen.value;
};

const close = () => {
  isOpen.value = false;
};

const clear = () => {
  query.value = '';
  since.value = '';
  until.value = '';
  results.value = [];
};

const resultDate = timestamp =>
  new Intl.DateTimeFormat(undefined, {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(new Date(timestamp * 1000));

const openResult = async message => {
  await store.dispatch('fetchMessagesAround', {
    conversationId: props.conversationId,
    messageId: message.id,
  });
  await nextTick();
  const element = document.getElementById(`message${message.id}`);
  element?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  element?.classList.add('ring-2', 'ring-n-brand', 'rounded-lg');
  window.setTimeout(() => {
    element?.classList.remove('ring-2', 'ring-n-brand', 'rounded-lg');
  }, 1800);
  close();
};
</script>

<template>
  <div v-on-click-outside="close" class="relative flex items-center">
    <Button
      v-tooltip="$t('CONVERSATION.MESSAGE_SEARCH.TOOLTIP')"
      size="sm"
      variant="ghost"
      color="slate"
      icon="i-lucide-search"
      class="rounded-md hover:bg-n-alpha-2"
      @click="toggle"
    />
    <div
      v-if="isOpen"
      class="absolute z-50 top-10 ltr:right-0 rtl:left-0 flex w-80 flex-col gap-2 rounded-xl border border-n-weak bg-n-solid-2 p-3 shadow-xl"
    >
      <div class="flex items-center justify-between">
        <h3 class="text-sm font-medium text-n-slate-12">
          {{ $t('CONVERSATION.MESSAGE_SEARCH.TITLE') }}
        </h3>
        <button
          v-if="hasFilters"
          type="button"
          class="text-xs text-n-brand hover:underline"
          @click="clear"
        >
          {{ $t('CONVERSATION.MESSAGE_SEARCH.CLEAR') }}
        </button>
      </div>
      <Input
        v-model="query"
        autofocus
        type="search"
        size="sm"
        :placeholder="$t('CONVERSATION.MESSAGE_SEARCH.PLACEHOLDER')"
        @keydown.esc="close"
      />
      <div class="grid grid-cols-2 gap-2">
        <label class="flex flex-col gap-1 text-xs text-n-slate-11">
          {{ $t('CONVERSATION.MESSAGE_SEARCH.FROM_DATE') }}
          <input
            v-model="since"
            type="date"
            :max="until || undefined"
            class="!mb-0 !h-8 !rounded-lg !border-n-weak !bg-n-alpha-black2 !px-2 !text-xs text-n-slate-12"
          />
        </label>
        <label class="flex flex-col gap-1 text-xs text-n-slate-11">
          {{ $t('CONVERSATION.MESSAGE_SEARCH.TO_DATE') }}
          <input
            v-model="until"
            type="date"
            :min="since || undefined"
            class="!mb-0 !h-8 !rounded-lg !border-n-weak !bg-n-alpha-black2 !px-2 !text-xs text-n-slate-12"
          />
        </label>
      </div>
      <div class="max-h-72 overflow-y-auto rounded-lg">
        <div v-if="isLoading" class="flex justify-center p-5">
          <Spinner class="text-n-brand" />
        </div>
        <p
          v-else-if="hasFilters && !results.length"
          class="p-4 text-center text-xs text-n-slate-10"
        >
          {{ $t('CONVERSATION.MESSAGE_SEARCH.NO_RESULTS') }}
        </p>
        <template v-else>
          <button
            v-for="message in results"
            :key="message.id"
            type="button"
            class="flex w-full flex-col gap-1 border-b border-n-weak p-2 text-left last:border-0 hover:bg-n-alpha-2"
            @click="openResult(message)"
          >
            <span class="line-clamp-2 text-xs text-n-slate-12">
              {{ message.content }}
            </span>
            <span class="text-xxs text-n-slate-10">
              {{ resultDate(message.created_at) }}
            </span>
          </button>
        </template>
      </div>
    </div>
  </div>
</template>
